/*
 * Locale state: 'en' | 'pt-BR'. The provider (LocaleProvider.tsx) applies
 * `document.documentElement.lang` and persists the choice to localStorage
 * under LOCALE_STORAGE_KEY. Default comes from the browser (`pt*` → `pt-BR`,
 * else `en`).
 *
 * The context also carries `t(key, params)` — the UI-string lookup over the
 * catalogs in `strings/` (en.ts is the source of truth; pt-BR.ts must match).
 */
import { createContext, useContext } from 'react';

import en from './strings/en';
import ptBR from './strings/pt-BR';

export type Locale = 'en' | 'pt-BR';

export const LOCALE_STORAGE_KEY = 'screenatlas.locale';

const LOCALES: readonly Locale[] = ['en', 'pt-BR'];

export type InterpolationParams = Record<string, string | number>;

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Translate a UI string; missing keys return the key itself (dev signal). */
  t: (key: string, params?: InterpolationParams) => string;
}

const DICTS: Record<Locale, Record<string, string>> = {
  en,
  'pt-BR': ptBR,
};

function interpolate(template: string, params?: InterpolationParams): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const value = params[key];
    return value !== undefined ? String(value) : `{{${key}}}`;
  });
}

/** Look up a string for a locale; an unknown key returns the key itself. */
export function tForLocale(
  locale: Locale,
  key: string,
  params?: InterpolationParams,
): string {
  return interpolate(DICTS[locale][key] ?? key, params);
}

export const LocaleContext = createContext<LocaleContextValue | null>(null);

/** Read the current locale choice. Throws outside a <LocaleProvider>. */
export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used within <LocaleProvider>');
  return value;
}

/**
 * Read the locale without throwing — for isolated component tests and leaf
 * components that render outside the provider tree. Falls back to the
 * module-level mirror (getLocale), so `t()` still resolves through the
 * catalogs with the same default-locale behaviour.
 */
export function useLocaleOptional(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (value) return value;
  return {
    locale: getLocale(),
    setLocale: setLocaleForHttp,
    t: (key, params) => translate(key, params),
  };
}

/** Just the `t` function, for components that only translate. */
export function useT() {
  return useLocale().t;
}

/** The browser's language mapped to a supported locale (`pt*` → `pt-BR`). */
export function browserLocale(): Locale {
  try {
    const lang = navigator.language?.toLowerCase() ?? '';
    return lang.startsWith('pt') ? 'pt-BR' : 'en';
  } catch {
    return 'en';
  }
}

/**
 * `?lang=` on the entry URL — the landing site passes its own language
 * (`/br/…` → `pt-BR`) so a visitor arriving from it keeps that language.
 */
export function urlLocale(): Locale | null {
  try {
    const lang = new URLSearchParams(window.location.search).get('lang')?.toLowerCase() ?? '';
    if (lang.startsWith('pt') || lang === 'br') return 'pt-BR';
    if (lang.startsWith('en')) return 'en';
  } catch {
    // no window (tests, SSR tooling) — fall through
  }
  return null;
}

/**
 * The starting locale: a URL `?lang=` wins and is persisted (it is an explicit
 * choice made on the landing site); else the stored choice; else the browser.
 */
export function readStoredLocale(): Locale {
  const fromUrl = urlLocale();
  if (fromUrl) {
    storeLocale(fromUrl);
    return fromUrl;
  }
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return LOCALES.includes(stored as Locale) ? (stored as Locale) : browserLocale();
  } catch {
    return browserLocale();
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // storage unavailable (private mode, quota) — the session still works
  }
}

/*
 * Non-React access to the current locale. The HTTP client (api/http.ts) cannot
 * read React context, so <LocaleProvider> keeps this module-level value in sync
 * and request code calls getLocale() — the same shape tokenStore uses to
 * decouple auth state from React.
 */
let currentLocale: Locale | null = null;

/** Read the current locale outside React (e.g. when building request headers). */
export function getLocale(): Locale {
  if (currentLocale === null) currentLocale = readStoredLocale();
  return currentLocale;
}

/** Called by <LocaleProvider> to keep non-React readers in sync. */
export function setLocaleForHttp(locale: Locale): void {
  currentLocale = locale;
}

/**
 * Non-React translation over the same module-level locale mirror that
 * `getLocale()` maintains — for toasts and api-layer strings that cannot read
 * React context. Unknown keys return the key itself (dev signal).
 */
export function translate(key: string, params?: InterpolationParams): string {
  return tForLocale(getLocale(), key, params);
}
